import { fetchAuthSession } from 'aws-amplify/auth';
import { AGENCY_CLAIMS_MARKER_CLAIM, AGENCY_CLAIMS_MARKER_VALUE } from '@ourbranch/staff-caller-scope';

// The backend trusts the agency claims (affinity, states) only on an ID token that
// StaffPreTokenGenerationLambda stamped with the Descope values and marked. A token issued without the
// marker (a transient read failure at issuance, or a permissions record missing its claims) is refused by
// every agency-scoped gate: account and offer pages, UW notes, LOE, search, documents (#36202).
export function hasVerifiedAgencyClaims(session) {
  const payload = session?.tokens?.idToken?.payload;
  return payload?.token_use === 'id' && payload?.[AGENCY_CLAIMS_MARKER_CLAIM] === AGENCY_CLAIMS_MARKER_VALUE;
}

// A revoked or expired refresh token: the session itself is gone, so session setup must still fail.
const SESSION_ENDED_ERRORS = new Set(['NotAuthorizedException', 'UserUnAuthenticatedException']);

// Forces one token refresh (which reruns the pre-token trigger) when the current ID token is unmarked.
// `verified: false` means the user still has no marked token and should sign in again. The refresh is
// optional: if it fails transiently or returns no tokens, the valid cached session is kept (unverified),
// so the user still lands logged in with the "sign in again" banner rather than being dropped to the
// login screen with no explanation.
export async function fetchAuthSessionWithVerifiedAgencyClaims() {
  const session = await fetchAuthSession();
  if (hasVerifiedAgencyClaims(session)) {
    return { session, verified: true };
  }
  let refreshed;
  try {
    refreshed = await fetchAuthSession({ forceRefresh: true });
  } catch (error) {
    if (SESSION_ENDED_ERRORS.has(error?.name)) {
      throw error;
    }
    console.warn('Forced token refresh failed; keeping the current session as unverified', error);
    return { session, verified: false };
  }
  if (!refreshed?.tokens?.idToken) {
    return { session, verified: false };
  }
  return { session: refreshed, verified: hasVerifiedAgencyClaims(refreshed) };
}

// For Amplify's background token refresh (Hub 'tokenRefresh'), which reruns the pre-token trigger
// mid-session. Reads the current session only: forcing another refresh here would fire tokenRefresh
// again and could loop.
export async function currentSessionHasVerifiedAgencyClaims() {
  return hasVerifiedAgencyClaims(await fetchAuthSession());
}
